import { Settings } from '@streamr/tv-engine';
import { executeSoon } from '@streamr/tv-utils';
import { Mutex } from 'async-mutex';
import { EventEmitter } from 'eventemitter3';
import { isIOS } from 'react-device-detect';
import { DefaultVideoConstraints } from '../../consts';
import { ChosenMediaStore } from './ChosenMediaStore';
import { MediaDeviceList } from './MediaDeviceList';
import { MediaServiceOutput } from './MediaServiceOutput';
/**
 * How long a capture track may stay muted after a 'mute' event (or after
 * arriving muted from getUserMedia) before the watchdog re-acquires it.
 * Transient mutes — orientation changes, brief app switches — unmute well
 * within this window.
 */
const STUCK_MUTED_GRACE_MS = 2000;
const MAX_VIDEO_RECOVERY_ATTEMPTS = 3;
export class MediaService extends EventEmitter {
    // Singleton pattern implementation
    static instance;
    // Return the full singleton instance of the MediaService. Do not use this
    // method in React components.
    static getInstance() {
        if (!MediaService.instance) {
            MediaService.instance = new MediaService();
        }
        return MediaService.instance;
    }
    /**
     * @returns A notification interface that contains only void-returning methods
     * to be used in React components.
     */
    static getNotificationInterface() {
        return MediaService.getInstance();
    }
    /**
     * @returns A set of reactive getters to be used in React components.
     */
    static getOutput() {
        return MediaServiceOutput.getReactiveOutput();
    }
    // ------------- Singleton implementation end
    output;
    outputMediaStream;
    permissionedAudioDevices;
    permissionedVideoDevices;
    connectedVideoDevices;
    connectedAudioDevices;
    audioStream;
    videoStream;
    displayMediaStream;
    audioMuted = false;
    videoMuted = false;
    captureStarted = false;
    capturePolicy = 'default';
    /**
     * Serializes every camera/microphone (re)acquisition. Stream updates can
     * be triggered concurrently — a capture start, a 'devicechange' event, a
     * resolution change — and two overlapping runs would each stop the other's
     * tracks and race their getUserMedia results, installing whichever
     * resolves last. On iOS (single capture session) the loser's tracks are
     * additionally muted by the OS, so an out-of-order install publishes a
     * dead, frame-less track.
     */
    streamUpdateMutex = new Mutex();
    videoRecoveryAttempts = 0;
    videoWatchdogToken = 0;
    videoWidth = 1280;
    videoHeight = 720;
    additionalStreams = new Map();
    constructor() {
        super();
        this.output = new MediaServiceOutput();
        this.permissionedAudioDevices = new MediaDeviceList();
        this.permissionedVideoDevices = new MediaDeviceList();
        this.connectedVideoDevices = new MediaDeviceList();
        this.connectedAudioDevices = new MediaDeviceList();
        // Listen to device add/remove events
        if (navigator.mediaDevices) {
            navigator.mediaDevices.addEventListener('devicechange', () => {
                this.updatePermissionedDevices().catch((error) => {
                    console.error('Error updating devices', error);
                });
                this.updateConnectedMediaDevices().catch((error) => {
                    console.error('Error updating connected devices', error);
                });
            });
        }
        this.updatePermissionedDevices().catch((error) => {
            console.error('Error initializing permissions', error);
        });
        this.updateConnectedMediaDevices().catch((error) => {
            console.error('Error updating connected devices', error);
        });
    }
    async init() {
        // ToDo: Implement initialization
    }
    getChosenAudioDevice() {
        const chosenAudioDevice = ChosenMediaStore.getInstance().getChosenAudioDevice();
        if (chosenAudioDevice &&
            this.permissionedAudioDevices.has(chosenAudioDevice)) {
            return chosenAudioDevice;
        }
        if (this.permissionedAudioDevices.getDevices().length > 0) {
            const device = this.permissionedAudioDevices.getDevices()[0];
            ChosenMediaStore.getInstance().setChosenAudioDevice(device);
            return device;
        }
        return undefined;
    }
    getChosenVideoDevice() {
        const chosenVideoDevice = ChosenMediaStore.getInstance().getChosenVideoDevice();
        if (chosenVideoDevice &&
            this.permissionedVideoDevices.has(chosenVideoDevice)) {
            return chosenVideoDevice;
        }
        if (this.permissionedVideoDevices.getDevices().length > 0) {
            const device = this.permissionedVideoDevices.getDevices()[0];
            ChosenMediaStore.getInstance().setChosenVideoDevice(device);
            return device;
        }
        return undefined;
    }
    async updatePermissionedDevices() {
        const devices = await navigator.mediaDevices.enumerateDevices();
        // If a device has no label, we do not have permission to use it,
        // so we filter them out
        const audioDevices = devices.filter((device) => {
            if (device.kind === 'audioinput') {
                return device.label && device.label.length > 0;
            }
            return false;
        });
        const videoDevices = devices.filter((device) => {
            if (device.kind === 'videoinput') {
                return device.label && device.label.length > 0;
            }
            return false;
        });
        // Update the devices in the output state if they have changed
        const audioDevicesChanged = this.permissionedAudioDevices.updateDevices(audioDevices);
        if (audioDevicesChanged) {
            if (this.permissionedAudioDevices.getDevices().length > 0) {
                this.output.setMicrophonePermissioned(true);
            }
            else {
                this.output.setMicrophonePermissioned(false);
            }
            this.output.setAudioDevices(this.permissionedAudioDevices.getDevices());
        }
        const videoDevicesChanged = this.permissionedVideoDevices.updateDevices(videoDevices);
        if (videoDevicesChanged) {
            if (this.permissionedVideoDevices.getDevices().length > 0) {
                this.output.setCameraPermissioned(true);
            }
            else {
                this.output.setCameraPermissioned(false);
            }
            this.output.setVideoDevices(this.permissionedVideoDevices.getDevices());
        }
        // If the devices have changed, update the media streams — but skip
        // re-acquisition while the active capture is unaffected. iOS WebKit
        // fires 'devicechange' when capture starts or stops (device labels
        // are only exposed to pages that capture), so re-acquiring here
        // unconditionally would stop the just-acquired tracks and reopen the
        // camera in rapid succession — the exact pattern that makes iOS hand
        // back muted, frame-less capture tracks.
        if (audioDevicesChanged &&
            this.needsReacquireAfterDeviceChange('audio')) {
            await this.updateAudioStream();
        }
        if (videoDevicesChanged &&
            this.needsReacquireAfterDeviceChange('video')) {
            await this.updateVideoStream();
        }
    }
    needsReacquireAfterDeviceChange(kind) {
        if (!this.captureStarted) {
            // The update call is a cheap no-op apart from refreshing the
            // selected-device output; let it run.
            return true;
        }
        const stream = kind === 'audio' ? this.audioStream : this.videoStream;
        const track = kind === 'audio'
            ? stream?.getAudioTracks()[0]
            : stream?.getVideoTracks()[0];
        if (!track) {
            // Nothing captured for this kind: acquire only if a usable
            // device is now wanted (e.g. the device list just went from
            // empty to populated).
            return kind === 'audio'
                ? this.wantsAudioCapture()
                : this.wantsVideoCapture();
        }
        if (track.readyState !== 'live') {
            return true;
        }
        const deviceId = track.getSettings().deviceId;
        const list = kind === 'audio'
            ? this.permissionedAudioDevices
            : this.permissionedVideoDevices;
        const stillPresent = deviceId !== undefined &&
            list.getDevices().some((d) => d.deviceId === deviceId);
        if (stillPresent) {
            console.log(`[MediaService] devicechange: ${kind} device list changed but active capture is unaffected — keeping current track`);
            return false;
        }
        return true;
    }
    async updateConnectedMediaDevices() {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter((device) => device.kind === 'audioinput');
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        this.connectedAudioDevices.updateDevices(audioDevices);
        this.connectedVideoDevices.updateDevices(videoDevices);
        this.output.setConnectedAudioDevices(this.connectedAudioDevices.getDevices());
        this.output.setConnectedVideoDevices(this.connectedVideoDevices.getDevices());
    }
    async requestMediaPermissions({ audio = false, video = false, }) {
        if (this.capturePolicy === 'screen-only') {
            return;
        }
        let mediaStream;
        mediaStream = await navigator.mediaDevices.getUserMedia({
            audio,
            video: video ? DefaultVideoConstraints : false,
        });
        for (const track of mediaStream.getTracks()) {
            track.stop();
        }
        await this.updatePermissionedDevices();
        // await this.updateMediaStream();
    }
    publishMediaStream() {
        const tracks = [];
        // Check if display media tracks are still active (not ended by browser's stop sharing)
        const activeDisplayAudioTracks = this.displayMediaStream
            ?.getAudioTracks()
            .filter((track) => track.readyState === 'live') || [];
        const activeDisplayVideoTracks = this.displayMediaStream
            ?.getVideoTracks()
            .filter((track) => track.readyState === 'live') || [];
        // If no active display tracks, clear the display media stream
        if (this.displayMediaStream &&
            activeDisplayAudioTracks.length === 0 &&
            activeDisplayVideoTracks.length === 0) {
            this.displayMediaStream = undefined;
        }
        // Prioritize active display media stream video tracks
        if (activeDisplayVideoTracks.length > 0) {
            tracks.push(...activeDisplayVideoTracks);
        }
        else if (this.videoStream) {
            tracks.push(...this.videoStream.getVideoTracks());
        }
        // A shared tab's audio is published beside the microphone, not in
        // its place: the sharer keeps talking over the tab's sound.
        if (this.audioStream) {
            tracks.push(...this.audioStream.getAudioTracks());
        }
        tracks.push(...activeDisplayAudioTracks);
        this.outputMediaStream = new MediaStream(tracks);
        const videoTrack = this.outputMediaStream.getVideoTracks()[0];
        const isUserFacingVideo = videoTrack &&
            (videoTrack.getSettings().facingMode === 'user' ||
                /facetime hd camera|integrated/i.test(videoTrack.label));
        this.output.setMediaStreamData({
            mediaStream: this.outputMediaStream,
            isUserFacingVideo: isUserFacingVideo,
            isSharingScreen: this.displayMediaStream !== undefined,
            // Check active display media tracks, not just their existence
            isSharingScreenAudio: activeDisplayAudioTracks.length > 0,
            isSharingScreenVideo: activeDisplayVideoTracks.length > 0,
            hasAudio: this.outputMediaStream.getAudioTracks().length > 0,
            hasVideo: this.outputMediaStream.getVideoTracks().length > 0,
            hasTracks: this.outputMediaStream.getTracks().length > 0,
        });
        this.emit('mediaStreamChanged');
    }
    /**
     * iOS WebKit allows only one active capture session: acquiring a new
     * stream — even an audio-only one, e.g. for a microphone change —
     * interrupts the running camera capture, and its video track never
     * recovers. On such platforms audio and video must be re-acquired
     * together in a single getUserMedia call. The localStorage flag lets
     * e2e tests exercise this path on desktop browsers.
     */
    isSingleCaptureSessionPlatform() {
        try {
            if (globalThis.localStorage?.getItem('singleCaptureSession') === '1') {
                return true;
            }
        }
        catch {
            // localStorage unavailable
        }
        return isIOS;
    }
    wantsAudioCapture() {
        return (!this.audioMuted &&
            this.getChosenAudioDevice() !== undefined &&
            this.capturePolicy !== 'screen-only');
    }
    wantsVideoCapture() {
        return (!this.videoMuted &&
            this.getChosenVideoDevice() !== undefined &&
            this.capturePolicy !== 'screen-only');
    }
    buildAudioConstraints(device) {
        return {
            deviceId: {
                exact: device.deviceId,
            },
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: Settings.audioSampleRate,
        };
    }
    buildVideoConstraints(device) {
        return {
            ...DefaultVideoConstraints,
            width: { ideal: this.videoWidth, max: this.videoWidth },
            height: { ideal: this.videoHeight, max: this.videoHeight },
            deviceId: {
                exact: device.deviceId,
            },
        };
    }
    /**
     * iOS WebKit mutes (or ends) live capture tracks on interruptions —
     * camera contention, app switches, and rapid stop/start cycles such as
     * a leave-stage -> rejoin. A muted track stays 'live' but delivers no
     * frames, so the encoder silently starves and the remote side is left
     * showing a placeholder; nothing in the platform unmutes it. Until now
     * the only recovery was the user toggling the camera off and on (a
     * fresh getUserMedia). Watch the primary video track and perform that
     * re-acquisition automatically instead.
     */
    armVideoTrackWatchdog(track) {
        const token = ++this.videoWatchdogToken;
        const isCurrent = () => token === this.videoWatchdogToken &&
            this.captureStarted &&
            this.videoStream?.getVideoTracks()[0] === track;
        const recover = (reason) => {
            if (!isCurrent() || !this.wantsVideoCapture()) {
                return;
            }
            if (this.videoRecoveryAttempts >= MAX_VIDEO_RECOVERY_ATTEMPTS) {
                console.error(`[MediaService] video track ${reason} — giving up after ${this.videoRecoveryAttempts} recovery attempts`);
                return;
            }
            this.videoRecoveryAttempts++;
            console.warn(`[MediaService] video track ${reason} — re-acquiring (attempt ${this.videoRecoveryAttempts}/${MAX_VIDEO_RECOVERY_ATTEMPTS})`);
            this.updateVideoStream().catch((e) => {
                console.error('[MediaService] video track recovery failed', e);
            });
        };
        const scheduleStuckMutedCheck = () => {
            setTimeout(() => {
                if (isCurrent() && track.muted && track.readyState === 'live') {
                    recover('stuck muted');
                }
            }, STUCK_MUTED_GRACE_MS);
        };
        track.addEventListener('mute', () => {
            console.warn('[MediaService] video track muted by the OS');
            scheduleStuckMutedCheck();
        });
        track.addEventListener('unmute', () => {
            console.log('[MediaService] video track unmuted');
            this.videoRecoveryAttempts = 0;
        });
        track.addEventListener('ended', () => {
            recover('ended unexpectedly');
        });
        if (track.muted) {
            console.warn('[MediaService] video track arrived muted from getUserMedia');
            scheduleStuckMutedCheck();
        }
    }
    updateAudioStream() {
        return this.streamUpdateMutex.runExclusive(() => this.updateAudioStreamLocked());
    }
    updateVideoStream() {
        return this.streamUpdateMutex.runExclusive(() => this.updateVideoStreamLocked());
    }
    updateMediaStreams() {
        return this.streamUpdateMutex.runExclusive(() => this.updateMediaStreamsLocked());
    }
    /**
     * Re-acquires audio and video in one getUserMedia call — see
     * isSingleCaptureSessionPlatform(). Callers must hold streamUpdateMutex.
     */
    async updateCombinedStreamLocked() {
        const chosenAudioDevice = this.getChosenAudioDevice();
        const chosenVideoDevice = this.getChosenVideoDevice();
        if (chosenAudioDevice) {
            this.output.setSelectedAudioDevice(chosenAudioDevice);
        }
        if (chosenVideoDevice) {
            this.output.setSelectedVideoDevice(chosenVideoDevice);
        }
        if (!this.captureStarted) {
            return;
        }
        this.audioStream?.getAudioTracks().forEach((track) => {
            track.stop();
        });
        this.audioStream = undefined;
        this.videoStream?.getVideoTracks().forEach((track) => {
            track.stop();
        });
        this.videoStream = undefined;
        const wantsAudio = this.wantsAudioCapture();
        const wantsVideo = this.wantsVideoCapture();
        if (wantsAudio || wantsVideo) {
            const combinedStream = await navigator.mediaDevices.getUserMedia({
                audio: wantsAudio && chosenAudioDevice
                    ? this.buildAudioConstraints(chosenAudioDevice)
                    : false,
                video: wantsVideo && chosenVideoDevice
                    ? this.buildVideoConstraints(chosenVideoDevice)
                    : false,
            });
            // Check if capture wasn't stopped in the meantime
            if (!this.captureStarted) {
                combinedStream.getTracks().forEach((track) => {
                    track.stop();
                });
                return;
            }
            const audioTracks = combinedStream.getAudioTracks();
            if (audioTracks.length > 0) {
                this.audioStream = new MediaStream(audioTracks);
            }
            const videoTracks = combinedStream.getVideoTracks();
            if (videoTracks.length > 0) {
                this.videoStream = new MediaStream(videoTracks);
                this.armVideoTrackWatchdog(videoTracks[0]);
            }
        }
        this.publishMediaStream();
    }
    async updateAudioStreamLocked() {
        if (this.isSingleCaptureSessionPlatform() && this.wantsVideoCapture()) {
            await this.updateCombinedStreamLocked();
            return;
        }
        const chosenAudioDevice = this.getChosenAudioDevice();
        if (chosenAudioDevice) {
            this.output.setSelectedAudioDevice(chosenAudioDevice);
        }
        if (!this.captureStarted) {
            return;
        }
        // Stop existing audio tracks
        this.audioStream?.getAudioTracks().forEach((track) => {
            track.stop();
        });
        this.audioStream = undefined;
        if (!this.audioMuted &&
            chosenAudioDevice &&
            this.capturePolicy !== 'screen-only') {
            const tempAudioStream = await navigator.mediaDevices.getUserMedia({
                audio: this.buildAudioConstraints(chosenAudioDevice),
                video: false,
            });
            // Check if capture wasn't stopped in the meantime
            if (!this.captureStarted) {
                tempAudioStream.getTracks().forEach((track) => {
                    track.stop();
                });
                return;
            }
            this.audioStream = tempAudioStream;
        }
        this.publishMediaStream();
    }
    async updateVideoStreamLocked() {
        if (this.isSingleCaptureSessionPlatform() && this.wantsAudioCapture()) {
            await this.updateCombinedStreamLocked();
            return;
        }
        const chosenVideoDevice = this.getChosenVideoDevice();
        if (chosenVideoDevice) {
            this.output.setSelectedVideoDevice(chosenVideoDevice);
        }
        if (!this.captureStarted) {
            return;
        }
        // Stop existing video tracks
        this.videoStream?.getVideoTracks().forEach((track) => {
            track.stop();
        });
        this.videoStream = undefined;
        if (!this.videoMuted &&
            chosenVideoDevice &&
            this.capturePolicy !== 'screen-only') {
            const tempVideoStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: this.buildVideoConstraints(chosenVideoDevice),
            });
            // Check if capture wasn't stopped in the meantime
            if (!this.captureStarted) {
                tempVideoStream.getTracks().forEach((track) => {
                    track.stop();
                });
                return;
            }
            this.videoStream = tempVideoStream;
            const actualTrack = tempVideoStream.getVideoTracks()[0];
            if (actualTrack) {
                this.armVideoTrackWatchdog(actualTrack);
                const s = actualTrack.getSettings();
                console.log('[MediaService] camera resolution', {
                    requested: `${this.videoWidth}x${this.videoHeight}`,
                    actual: `${s.width}x${s.height}`,
                    frameRate: s.frameRate,
                });
            }
        }
        this.publishMediaStream();
    }
    async updateMediaStreamsLocked() {
        // A single combined acquisition also avoids the WebKit worklet
        // freeze the sequential path below works around.
        if (this.isSingleCaptureSessionPlatform()) {
            await this.updateCombinedStreamLocked();
            return;
        }
        // Video first: WebKit freezes the AudioWorklet render thread for ~800ms
        // when getUserMedia({video}) resolves while a worklet process() is active.
        // Completing video before audio ensures the camera device is already
        // active by the time the audio worklet starts.
        await this.updateVideoStreamLocked();
        await this.updateAudioStreamLocked();
    }
    async updateDisplayMediaStream(surfaceType, includeAudio = true) {
        const newDisplayMediaStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                displaySurface: surfaceType,
                frameRate: {
                    ideal: Settings.videoEncoderDefaultFrameRate,
                },
            },
            audio: includeAudio
                ? {
                    sampleRate: Settings.audioSampleRate,
                    echoCancellation: true,
                    noiseSuppression: true,
                    suppressLocalAudioPlayback: true,
                }
                : false,
        });
        // Stop camera video when screen sharing
        this.videoStream?.getVideoTracks().forEach((track) => {
            track.stop();
        });
        this.videoStream = undefined;
        this.displayMediaStream = newDisplayMediaStream;
        for (const track of this.displayMediaStream.getTracks()) {
            track.isScreenShare = true;
            // Listen for track ended events (browser's stop sharing button)
            track.addEventListener('ended', () => {
                this.onDisplayMediaTrackEnded();
            });
        }
        if (this.displayMediaStream.getVideoTracks().length > 0) {
            const videoTrack = this.displayMediaStream.getVideoTracks()[0];
            videoTrack.applyConstraints({
                frameRate: {
                    min: Settings.videoEncoderDefaultFrameRate,
                },
            });
        }
        this.publishMediaStream();
    }
    /**
     * Handles a display-capture track ending (the browser's "Stop sharing"
     * button, or the shared tab/window closing). Fires once per share: the
     * first ended track that leaves no live display tracks behind clears the
     * stream and emits `screenShareEnded`.
     */
    onDisplayMediaTrackEnded() {
        if (this.displayMediaStream === undefined) {
            return;
        }
        const anyLive = this.displayMediaStream
            .getTracks()
            .some((track) => track.readyState === 'live');
        if (anyLive) {
            // Only some of the tracks ended (e.g. the shared tab's audio):
            // republish so the ended track leaves the output.
            this.publishMediaStream();
            return;
        }
        this.displayMediaStream = undefined;
        this.emit('screenShareEnded');
        if (this.capturePolicy === 'default') {
            // Restart camera when screen share ends
            this.updateVideoStream()
                .then(() => {
                this.publishMediaStream();
            })
                .catch((e) => {
                console.error('Error restarting camera after screen share ended', e);
            });
        }
        else {
            this.publishMediaStream();
        }
    }
    // Getter for other services
    getOutputMediaStream() {
        return this.outputMediaStream;
    }
    /**
     * Sets the capture policy. Under 'screen-only' getUserMedia is never
     * called (no camera/microphone prompts or capture) and stopping a screen
     * share does not fall back to the camera. In-memory only — callers that
     * need the policy to survive a reload must re-apply it before capture
     * can start.
     */
    setCapturePolicy(policy) {
        this.capturePolicy = policy;
    }
    isScreenShareActive() {
        return (this.displayMediaStream
            ?.getTracks()
            .some((track) => track.readyState === 'live') ?? false);
    }
    /**
     * Starts screen capture and resolves when the user has accepted the
     * browser's source picker (rejects on dismissal, e.g. NotAllowedError).
     * Unlike notifyScreenShareRequested this is awaitable, and audio capture
     * (only offered by Chrome for tab shares) can be declined up front.
     */
    async requestScreenShare(surfaceType, opts) {
        await this.updateDisplayMediaStream(surfaceType, opts?.audio ?? true);
    }
    /**
     * Get the video track directly from its source stream.
     * This is needed for Safari where a track must be transferred from its original stream.
     */
    getSourceVideoTrack() {
        // Prioritize display media video tracks
        const displayVideoTrack = this.displayMediaStream
            ?.getVideoTracks()
            .find((track) => track.readyState === 'live');
        if (displayVideoTrack) {
            return displayVideoTrack;
        }
        return this.videoStream
            ?.getVideoTracks()
            .find((track) => track.readyState === 'live');
    }
    /**
     * Get the microphone audio track directly from its source stream.
     * This is needed for Safari where a track must be transferred from its original stream.
     * A shared tab's audio is a separate track: see getDisplayAudioTrack().
     */
    getSourceAudioTrack() {
        return this.audioStream?.getAudioTracks()[0];
    }
    /**
     * The live audio track of the current display capture (a shared tab's
     * audio), if any. It is published beside the microphone track, never in
     * its place.
     */
    getDisplayAudioTrack() {
        return this.displayMediaStream
            ?.getAudioTracks()
            .find((track) => track.readyState === 'live');
    }
    // Notifications to be called by React components
    notifyAudioDeviceSelected(audioDevice) {
        if (ChosenMediaStore.getInstance().getChosenAudioDevice() !==
            audioDevice) {
            ChosenMediaStore.getInstance().setChosenAudioDevice(audioDevice);
            this.updateAudioStream().catch((e) => {
                console.error(e);
            });
        }
    }
    notifyVideoDeviceSelected(videoDevice) {
        if (ChosenMediaStore.getInstance().getChosenVideoDevice() !==
            videoDevice) {
            ChosenMediaStore.getInstance().setChosenVideoDevice(videoDevice);
            this.updateVideoStream().catch((e) => {
                console.error(e);
            });
        }
    }
    notifyMediaPermissionsRequested({ audio = false, video = false, }) {
        this.requestMediaPermissions({ audio, video }).catch((e) => {
            console.error(e);
        });
    }
    notifyAudioMutingChangeRequested(muted) {
        if (this.audioMuted !== muted) {
            this.audioMuted = muted;
            this.output.setAudioMuted(muted);
            // Instead of recreating the stream, just enable/disable the existing audio track
            const audioTracks = this.audioStream?.getAudioTracks() ?? [];
            for (const track of audioTracks) {
                track.enabled = !muted;
            }
        }
    }
    notifyVideoMutingChangeRequested(muted) {
        if (this.videoMuted !== muted) {
            this.videoMuted = muted;
            this.output.setVideoMuted(muted);
            this.videoRecoveryAttempts = 0;
            this.updateVideoStream().catch((e) => {
                console.error(e);
            });
        }
    }
    setVideoResolution(width, height) {
        if (this.videoWidth === width && this.videoHeight === height) {
            return;
        }
        this.videoWidth = width;
        this.videoHeight = height;
        if (this.captureStarted) {
            this.updateVideoStream().catch(console.error);
        }
    }
    static setAudioSessionType(type) {
        const nav = navigator;
        if (nav.audioSession) {
            nav.audioSession.type = type;
        }
    }
    notifyStartCaptureRequested() {
        if (this.captureStarted) {
            return;
        }
        MediaService.setAudioSessionType('play-and-record');
        this.captureStarted = true;
        this.videoRecoveryAttempts = 0;
        executeSoon(() => this.updateMediaStreams());
    }
    notifyStopCaptureRequested() {
        this.captureStarted = false;
        MediaService.setAudioSessionType('playback');
        this.audioStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.audioStream = undefined;
        this.videoStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.videoStream = undefined;
        this.displayMediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.displayMediaStream = undefined;
        for (const [trackId, stream] of this.additionalStreams) {
            stream.getTracks().forEach((track) => {
                track.stop();
            });
            this.emit('additionalTrackRemoved', trackId);
        }
        this.additionalStreams.clear();
        this.outputMediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.outputMediaStream = undefined;
        this.publishMediaStream();
    }
    notifyScreenShareRequested(surfaceType) {
        this.requestScreenShare(surfaceType).catch((e) => {
            console.error(e);
        });
    }
    notifyScreenShareStopped() {
        if (this.displayMediaStream === undefined) {
            return;
        }
        // Manually stopped tracks do not fire 'ended', so the shared
        // teardown below won't double-run via onDisplayMediaTrackEnded.
        this.displayMediaStream.getTracks().forEach((track) => {
            track.stop();
        });
        this.displayMediaStream = undefined;
        this.emit('screenShareEnded');
        if (this.capturePolicy === 'default') {
            // Restart camera when screen share is manually stopped
            this.updateVideoStream().catch((e) => {
                console.error('Error restarting camera after stopping screen share', e);
            });
        }
        else {
            this.publishMediaStream();
        }
    }
    notifyAddCamera(deviceId) {
        this.addAdditionalCamera(deviceId).catch((e) => {
            console.error('Error adding camera', e);
        });
    }
    notifyAddMicrophone(deviceId) {
        this.addAdditionalMicrophone(deviceId).catch((e) => {
            console.error('Error adding microphone', e);
        });
    }
    notifyShareScreen() {
        this.addScreenShare().catch((e) => {
            console.error('Error starting screen share', e);
        });
    }
    notifyRemoveLocalTrack(trackId) {
        const stream = this.additionalStreams.get(trackId);
        if (stream) {
            stream.getTracks().forEach((t) => {
                t.stop();
            });
            this.additionalStreams.delete(trackId);
            this.emit('additionalTrackRemoved', trackId);
            return;
        }
        // Also check display media tracks
        if (this.displayMediaStream) {
            const displayTrack = this.displayMediaStream
                .getTracks()
                .find((t) => t.id === trackId);
            if (displayTrack) {
                displayTrack.stop();
                const remaining = this.displayMediaStream
                    .getTracks()
                    .filter((t) => t.readyState === 'live');
                if (remaining.length === 0) {
                    this.displayMediaStream = undefined;
                }
                this.emit('additionalTrackRemoved', trackId);
            }
        }
    }
    async addAdditionalCamera(deviceId) {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
                ...DefaultVideoConstraints,
                width: {
                    ideal: Settings.cameraVideoWidth,
                    max: Settings.cameraVideoWidth,
                },
                height: {
                    ideal: Settings.cameraVideoHeight,
                    max: Settings.cameraVideoHeight,
                },
                deviceId: { exact: deviceId },
            },
        });
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack) {
            return;
        }
        this.additionalStreams.set(videoTrack.id, stream);
        videoTrack.addEventListener('ended', () => {
            this.additionalStreams.delete(videoTrack.id);
            this.emit('additionalTrackRemoved', videoTrack.id);
        });
        this.emit('additionalTrackAdded', videoTrack);
    }
    async addAdditionalMicrophone(deviceId) {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                deviceId: { exact: deviceId },
                echoCancellation: true,
                noiseSuppression: true,
                sampleRate: Settings.audioSampleRate,
            },
            video: false,
        });
        const audioTrack = stream.getAudioTracks()[0];
        if (!audioTrack) {
            return;
        }
        this.additionalStreams.set(audioTrack.id, stream);
        audioTrack.addEventListener('ended', () => {
            this.additionalStreams.delete(audioTrack.id);
            this.emit('additionalTrackRemoved', audioTrack.id);
        });
        this.emit('additionalTrackAdded', audioTrack);
    }
    async addScreenShare() {
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                frameRate: {
                    ideal: Settings.videoEncoderDefaultFrameRate,
                },
            },
            audio: {
                sampleRate: Settings.audioSampleRate,
                echoCancellation: true,
                noiseSuppression: true,
                suppressLocalAudioPlayback: true,
            },
        });
        for (const track of displayStream.getTracks()) {
            track.isScreenShare = true;
            this.additionalStreams.set(track.id, displayStream);
            track.addEventListener('ended', () => {
                this.additionalStreams.delete(track.id);
                this.emit('additionalTrackRemoved', track.id);
            });
            this.emit('additionalTrackAdded', track);
        }
    }
}
